import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { ButtonDeleteConfirm } from '@framework/ui/components/button/ButtonDeleteConfirm.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { Space } from 'antd';
import { type TFunction } from 'i18next';
import type Map from 'ol/Map';
import type VectorSource from 'ol/source/Vector';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type MapGeometryItem } from '../createMapSchema.ts';
import { getGeometryTypeFromFeatureGeoJson } from '../geometryTypeUtils.ts';
import { MapGeometryType } from '../IMapWidget.ts';
import {
  type MapUnit,
  MapUnitRender,
} from '../layers/rendering/MapUnitRender.tsx';
import { MapFlashFeatureButton } from './MapFlashFeatureButton.tsx';

/** Etichetta tradotta per il tipo di geometria OpenLayers. */
function getGeometryTypeLabel(t: TFunction, value: string | undefined): string {
  switch (value) {
    case MapGeometryType.Point:
      return t('map-widget.geometries.type.point', 'Punto');
    case MapGeometryType.LineString:
      return t('map-widget.geometries.type.line', 'Linea');
    case MapGeometryType.Polygon:
      return t('map-widget.geometries.type.polygon', 'Poligono');
    default:
      return value ?? '—';
  }
}

/**
 * Tipo geometria dell'item: usa `geometryType` se presente, altrimenti lo
 * deduce dal GeoJSON persistito. Le geometrie salvate prima
 * dell'introduzione del campo non lo hanno valorizzato.
 */
function resolveGeometryType(
  record: MapGeometryItem,
): MapGeometryType | undefined {
  return (
    record.geometryType ?? getGeometryTypeFromFeatureGeoJson(record.feature)
  );
}

/**
 * Misura pertinente di una geometria: area per i poligoni, lunghezza per
 * le linee. Restituisce `undefined` quando non c'è una misura da mostrare.
 */
function getGeometryMeasure(
  record: MapGeometryItem,
  geometryType: MapGeometryType | undefined,
): { value: number; unit: MapUnit } | undefined {
  switch (geometryType) {
    case MapGeometryType.Polygon:
      return record.area != null
        ? { value: record.area, unit: 'mq' }
        : undefined;
    case MapGeometryType.LineString:
      return record.length != null
        ? { value: record.length, unit: 'm' }
        : undefined;
    default:
      return undefined;
  }
}

export interface IMapGeometriesTableProps {
  editor: RelationEditor<MapGeometryItem>;
  readonly?: boolean;
  onRemove?: (id: string) => void;
  mapRef: React.RefObject<Map | null>;
  featuresSource: React.RefObject<VectorSource>;
  geometryLabelPrefix?: string;
}

export function MapGeometriesTable(props: IMapGeometriesTableProps) {
  const {
    editor,
    readonly,
    onRemove,
    mapRef,
    featuresSource,
    geometryLabelPrefix,
  } = props;
  const prefix = geometryLabelPrefix ?? '#';
  const { t } = useTranslation();

  const { visuallyDisabled } = useFormItemEnabled();

  // Risolve il tipo di ogni geometria una sola volta (evita JSON.parse
  // ripetuti del GeoJSON per i dati legacy senza `geometryType`).
  const geometryTypeById = useMemo(() => {
    const resolvedTypes: Record<string, MapGeometryType | undefined> = {};
    for (const item of editor.items) {
      resolvedTypes[item.id] = resolveGeometryType(item);
    }
    return resolvedTypes;
  }, [editor.items]);

  // La colonna Tipologia ha senso solo se sono presenti geometrie di tipi
  // diversi; con un unico tipo l'informazione è ridondante.
  const hasMultipleGeometryTypes =
    new Set(Object.values(geometryTypeById)).size > 1;

  // La colonna Misura si mostra solo se almeno una geometria ha una misura
  // (i punti non hanno né area né lunghezza).
  const hasAnyMeasure = editor.items.some(
    item => getGeometryMeasure(item, geometryTypeById[item.id]) != null,
  );

  return (
    <div>
      <TableData
        dataSource={editor.items}
        rowKey={record => record.id}
        columns={[
          {
            title: prefix,
            dataIndex: 'geometryIndex',
            key: 'geometryIndex',
            width: '80px',
            render: (value: number | undefined) => value ?? '—',
          },
          ...(hasMultipleGeometryTypes
            ? [
                {
                  title: t('map-widget.geometries.type-column', 'Tipologia'),
                  key: 'geometryType',
                  render: (_: unknown, record: MapGeometryItem) =>
                    getGeometryTypeLabel(t, geometryTypeById[record.id]),
                },
              ]
            : []),
          ...(hasAnyMeasure
            ? [
                {
                  title: t('map-widget.geometries.measure-column', 'Misura'),
                  key: 'measure',
                  render: (_: unknown, record: MapGeometryItem) => {
                    const measure = getGeometryMeasure(
                      record,
                      geometryTypeById[record.id],
                    );
                    return measure ? (
                      <MapUnitRender
                        value={measure.value}
                        unit={measure.unit}
                      />
                    ) : (
                      '—'
                    );
                  },
                },
              ]
            : []),
          {
            title: 'Azioni',
            key: 'actions',
            align: 'right',
            render: (value: MapGeometryItem) => {
              return (
                <Space>
                  <MapFlashFeatureButton
                    mapRef={mapRef}
                    featuresSource={featuresSource}
                    geometryIndex={value.geometryIndex}
                  />
                  <ButtonDeleteConfirm
                    iconOnlyButton
                    buttonDanger
                    disabled={readonly || visuallyDisabled}
                    tooltip="Elimina geometria"
                    okText="Elimina geometria"
                    title="Sei sicuro di voler eliminare la geometria selezionata?"
                    onConfirm={() => {
                      onRemove?.(value.id);
                    }}
                  />
                </Space>
              );
            },
          },
        ]}
      />
    </div>
  );
}
